/**
 * Normalizes API course payloads for admin preview/edit UIs.
 */

import { normalizeCourseContenidoDeep, stripArchivoTransientFieldsDeep } from "./temarioTree";

function clonePlainObject(value) {
  if (typeof structuredClone === "function") {
    return structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value));
}

/**
 * Returns trimmed non-empty tab names in order.
 * @param {unknown} pestanasCurso
 * @returns {string[]}
 */
export function normalizeCourseTabName(value) {
  return typeof value === "string" ? value.trim().replace(/\s+/g, " ") : "";
}

/**
 * Applies a tab-name keystroke without stripping spaces so compound names can be typed.
 * Duplicate names are allowed while typing; save/blur validation uses {@link getCourseTabsSaveBlocker}.
 *
 * @param {string[]} tabs
 * @param {number} index
 * @param {string} rawValue
 * @returns {{
 *   duplicate: boolean,
 *   tabs: string[],
 *   oldRaw: string,
 *   oldKey: string,
 *   newKey: string,
 *   shouldRemap: boolean,
 * }}
 */
export function applyLiveCourseTabName(tabs, index, rawValue) {
  const currentTabs = Array.isArray(tabs) ? tabs : [];
  const oldRaw = currentTabs[index] ?? "";
  const oldKey = normalizeCourseTabName(oldRaw);
  const liveName = typeof rawValue === "string" ? rawValue : "";
  const newKey = normalizeCourseTabName(liveName);
  const duplicate = Boolean(
    newKey &&
    currentTabs.some((p, i) => i !== index && normalizeCourseTabName(p) === newKey),
  );
  const nextTabs = [...currentTabs];
  nextTabs[index] = liveName;
  return {
    duplicate,
    tabs: nextTabs,
    oldRaw,
    oldKey,
    newKey,
    shouldRemap: Boolean(!duplicate && oldKey && newKey && oldKey !== newKey),
  };
}

/**
 * @param {string[]} uniqueKeys
 * @param {string[]} tabs
 * @returns {string[]}
 */
export function resizeUniqueTabKeys(uniqueKeys, tabs) {
  const next = Array.isArray(uniqueKeys) ? [...uniqueKeys] : [];
  const list = Array.isArray(tabs) ? tabs : [];
  while (next.length < list.length) {
    next.push(normalizeCourseTabName(list[next.length] || ""));
  }
  if (next.length > list.length) {
    next.length = list.length;
  }
  return next;
}

/**
 * Live tab rename that never blocks keystrokes. Content remaps from the last unique key.
 *
 * @param {string[]} tabs
 * @param {number} index
 * @param {string} rawValue
 * @param {string[]} lastUniqueKeys
 * @returns {{
 *   tabs: string[],
 *   duplicate: boolean,
 *   uniqueKeys: string[],
 *   shouldRemap: boolean,
 *   remapFrom: string,
 *   remapTo: string,
 *   oldRaw: string,
 *   oldKey: string,
 *   newKey: string,
 * }}
 */
export function applyCourseTabNameInput(tabs, index, rawValue, lastUniqueKeys) {
  const live = applyLiveCourseTabName(tabs, index, rawValue);
  const uniqueKeys = resizeUniqueTabKeys(lastUniqueKeys, live.tabs);
  const remapFrom = uniqueKeys[index] || live.oldKey;
  if (!live.duplicate) {
    uniqueKeys[index] = live.newKey;
  }
  const remapTo = live.duplicate ? "" : live.newKey;
  return {
    tabs: live.tabs,
    duplicate: live.duplicate,
    uniqueKeys,
    shouldRemap: Boolean(remapFrom && remapTo && remapFrom !== remapTo),
    remapFrom,
    remapTo,
    oldRaw: live.oldRaw,
    oldKey: live.oldKey,
    newKey: live.newKey,
  };
}

/**
 * @param {object} contenido
 * @param {string[]} candidateKeys
 * @returns {string}
 */
export function findTemarioContentKey(contenido, candidateKeys) {
  if (!contenido || typeof contenido !== "object" || Array.isArray(contenido)) {
    return "";
  }
  const seen = new Set();
  const keys = Array.isArray(candidateKeys) ? candidateKeys : [];
  for (const key of keys) {
    if (typeof key !== "string" || key === "" || seen.has(key)) {
      continue;
    }
    seen.add(key);
    if (Array.isArray(contenido[key])) {
      return key;
    }
  }
  return "";
}

export function getNamedCourseTabs(pestanasCurso) {
  if (!Array.isArray(pestanasCurso)) {
    return [];
  }
  return listOrderedCourseTabs(pestanasCurso);
}

/**
 * Unique trimmed tab labels in first-seen order.
 * @param {...(string[] | string | null | undefined)} tabGroups
 * @returns {string[]}
 */
export function listOrderedCourseTabs(...tabGroups) {
  const seen = new Set();
  /** @type {string[]} */
  const tabs = [];
  tabGroups.flat().forEach((value) => {
    const tab = normalizeCourseTabName(value);
    if (!tab || seen.has(tab)) {
      return;
    }
    seen.add(tab);
    tabs.push(tab);
  });
  return tabs;
}

/**
 * @param {Record<string, import("./temarioTree").TemarioNodo[]> | null | undefined} contenido
 * @param {string} tabLabel
 * @returns {import("./temarioTree").TemarioNodo[]}
 */
export function getContenidoNodesForTab(contenido, tabLabel) {
  const wanted = normalizeCourseTabName(tabLabel);
  if (!wanted || !contenido || typeof contenido !== "object" || Array.isArray(contenido)) {
    return [];
  }
  /** @type {import("./temarioTree").TemarioNodo[] | null} */
  let fallback = null;
  const keys = [tabLabel, wanted, ...Object.keys(contenido)];
  const seen = new Set();
  for (const key of keys) {
    if (typeof key !== "string" || seen.has(key)) {
      continue;
    }
    seen.add(key);
    if (normalizeCourseTabName(key) !== wanted) {
      continue;
    }
    const matched = contenido[key];
    if (!Array.isArray(matched)) {
      continue;
    }
    if (matched.length > 0) {
      return matched;
    }
    if (!fallback) {
      fallback = matched;
    }
  }
  return fallback || [];
}

/**
 * @param {unknown} pestanasCurso
 * @returns {boolean}
 */
export function hasUnnamedCourseTabs(pestanasCurso) {
  if (!Array.isArray(pestanasCurso)) {
    return false;
  }
  return pestanasCurso.some((p) => !normalizeCourseTabName(p));
}

/**
 * Normalized tab labels that appear more than once.
 * @param {unknown} pestanasCurso
 * @returns {string[]}
 */
export function listDuplicateCourseTabNames(pestanasCurso) {
  if (!Array.isArray(pestanasCurso)) {
    return [];
  }
  const seen = new Set();
  /** @type {string[]} */
  const duplicates = [];
  const reported = new Set();
  pestanasCurso.forEach((value) => {
    const tab = normalizeCourseTabName(value);
    if (!tab) {
      return;
    }
    if (seen.has(tab) && !reported.has(tab)) {
      duplicates.push(tab);
      reported.add(tab);
    }
    seen.add(tab);
  });
  return duplicates;
}

/**
 * @param {unknown} pestanasCurso
 * @returns {boolean}
 */
export function hasDuplicateCourseTabs(pestanasCurso) {
  return listDuplicateCourseTabNames(pestanasCurso).length > 0;
}

/**
 * Save-time blocker for course tab names. Unnamed slots take priority.
 * @param {unknown} pestanasCurso
 * @returns {"unnamed" | "duplicate" | null}
 */
export function getCourseTabsSaveBlocker(pestanasCurso) {
  if (!Array.isArray(pestanasCurso) || pestanasCurso.length === 0) {
    return null;
  }
  if (hasUnnamedCourseTabs(pestanasCurso)) {
    return "unnamed";
  }
  if (hasDuplicateCourseTabs(pestanasCurso)) {
    return "duplicate";
  }
  return null;
}

/**
 * Keeps only contenido entries whose keys match named course tabs (pestañas).
 * @param {Record<string, import("./temarioTree").TemarioNodo[]> | null | undefined} contenido
 * @param {string[] | null | undefined} pestanasCurso
 * @returns {Record<string, import("./temarioTree").TemarioNodo[]>}
 */
export function pickContenidoForPestanas(contenido, pestanasCurso) {
  const names = (Array.isArray(pestanasCurso) ? pestanasCurso : [])
    .map(normalizeCourseTabName)
    .filter(Boolean);
  if (names.length === 0) {
    return {};
  }
  const src = contenido && typeof contenido === "object" ? contenido : {};
  /** @type {Record<string, import("./temarioTree").TemarioNodo[]>} */
  const out = {};
  for (const name of names) {
    out[name] = getContenidoNodesForTab(src, name);
  }
  return out;
}

/**
 * Maps previous tab labels onto the current labels when a pestaña is renamed.
 *
 * @param {string[] | null | undefined} previousTabs
 * @param {string[] | null | undefined} nextTabs
 * @returns {Map<string, string>}
 */
export function buildTabRenameMap(previousTabs, nextTabs) {
  const previous = getNamedCourseTabs(previousTabs);
  const next = getNamedCourseTabs(nextTabs);
  const nextSet = new Set(next);
  /** @type {Map<string, string>} */
  const renameMap = new Map();
  const length = Math.min(previous.length, next.length);
  for (let index = 0; index < length; index += 1) {
    const fromName = previous[index];
    const toName = next[index];
    if (fromName && toName && fromName !== toName && !nextSet.has(fromName)) {
      renameMap.set(fromName, toName);
    }
  }
  return renameMap;
}

/**
 * @param {string[] | null | undefined} pestanas
 * @param {Map<string, string>} renameMap
 * @returns {string[]}
 */
export function applyTabRenameMapToPestanaList(pestanas, renameMap) {
  if (!Array.isArray(pestanas)) {
    return [];
  }
  return pestanas.map((value) => {
    const key = normalizeCourseTabName(value);
    const renamed = key ? renameMap.get(key) : "";
    return renamed || value;
  });
}

/**
 * Moves temario nodes onto renamed tab keys so Campus does not keep the old label.
 *
 * @param {Record<string, import("./temarioTree").TemarioNodo[]> | null | undefined} contenido
 * @param {string[] | null | undefined} nextTabs
 * @param {string[] | null | undefined} previousTabs
 * @returns {Record<string, import("./temarioTree").TemarioNodo[]>}
 */
export function rekeyContenidoForRenamedTabs(contenido, nextTabs, previousTabs) {
  const src = contenido && typeof contenido === "object" && !Array.isArray(contenido) ? { ...contenido } : {};
  const renameMap = buildTabRenameMap(previousTabs, nextTabs);
  renameMap.forEach((toName, fromName) => {
    const existing = getContenidoNodesForTab(src, toName);
    const fromNodes = getContenidoNodesForTab(src, fromName);
    if (existing.length === 0 && fromNodes.length > 0) {
      src[toName] = fromNodes;
    }
    Object.keys(src).forEach((key) => {
      if (normalizeCourseTabName(key) === fromName) {
        delete src[key];
      }
    });
  });
  return attachOrphanContenidoToPublishedTabs(src, nextTabs);
}

/**
 * Published tab labels are pestanasCurso. Leftover contenido keys are not extra tabs.
 *
 * @param {string[] | null | undefined} pestanasCurso
 * @param {Record<string, import("./temarioTree").TemarioNodo[]> | null | undefined} contenido
 * @returns {string[]}
 */
export function listPublishedCourseTabs(pestanasCurso, contenido) {
  const named = getNamedCourseTabs(pestanasCurso);
  if (named.length > 0) {
    return named;
  }
  const src = contenido && typeof contenido === "object" && !Array.isArray(contenido) ? contenido : {};
  return listOrderedCourseTabs(Object.keys(src));
}

function contenidoNodesSignature(nodes) {
  if (!Array.isArray(nodes) || nodes.length === 0) {
    return "";
  }
  return nodes
    .map((node) => {
      if (!node || typeof node !== "object") {
        return "";
      }
      const nombre = typeof node.nombre === "string" ? node.nombre : "";
      const archivos = Array.isArray(node.archivos)
        ? node.archivos
            .map((archivo) => (archivo && typeof archivo.nombre === "string" ? archivo.nombre : ""))
            .join(",")
        : "";
      return `${nombre}:${archivos}`;
    })
    .join("|");
}

/**
 * If a tab was renamed but contenido still uses the old key, attach that content to the new label.
 *
 * @param {Record<string, import("./temarioTree").TemarioNodo[]> | null | undefined} contenido
 * @param {string[] | null | undefined} pestanasCurso
 * @returns {Record<string, import("./temarioTree").TemarioNodo[]>}
 */
export function attachOrphanContenidoToPublishedTabs(contenido, pestanasCurso) {
  const tabs = listPublishedCourseTabs(pestanasCurso, contenido);
  const src = contenido && typeof contenido === "object" && !Array.isArray(contenido) ? contenido : {};
  const picked = pickContenidoForPestanas(src, tabs);
  const namedSet = new Set(tabs);
  const pickedSignatures = new Set(
    tabs
      .filter((tab) => Array.isArray(picked[tab]) && picked[tab].length > 0)
      .map((tab) => contenidoNodesSignature(picked[tab]))
      .filter(Boolean),
  );
  const orphanKeys = Object.keys(src).filter((key) => {
    const normalized = normalizeCourseTabName(key);
    if (!normalized || namedSet.has(normalized) || !Array.isArray(src[key]) || src[key].length === 0) {
      return false;
    }
    const signature = contenidoNodesSignature(src[key]);
    return !signature || !pickedSignatures.has(signature);
  });
  const emptyTabs = tabs.filter((tab) => !Array.isArray(picked[tab]) || picked[tab].length === 0);
  if (emptyTabs.length === 0 || orphanKeys.length === 0) {
    return picked;
  }
  const attachCount = Math.min(emptyTabs.length, orphanKeys.length);
  for (let index = 0; index < attachCount; index += 1) {
    picked[emptyTabs[index]] = src[orphanKeys[index]];
  }
  return picked;
}

/**
 * @param {string[] | null | undefined} pestanasCurso
 * @param {Record<string, import("./temarioTree").TemarioNodo[]> | null | undefined} contenido
 * @returns {{ pestanasCurso: string[], contenido: Record<string, import("./temarioTree").TemarioNodo[]> }}
 */
export function resolvePublishedCourseTabsAndContenido(pestanasCurso, contenido) {
  const tabs = listPublishedCourseTabs(pestanasCurso, contenido);
  return {
    pestanasCurso: tabs,
    contenido: attachOrphanContenidoToPublishedTabs(contenido, tabs),
  };
}

/**
 * @param {Array<{ pestanas?: string[] }>} tests
 * @param {Map<string, string>} renameMap
 * @returns {Array<{ pestanas?: string[] }>}
 */
export function applyTabRenameMapToTestsArray(tests, renameMap) {
  if (!Array.isArray(tests)) {
    return [];
  }
  if (!renameMap || renameMap.size === 0) {
    return tests;
  }
  return tests.map((test) => {
    if (!test || typeof test !== "object") {
      return test;
    }
    return {
      ...test,
      pestanas: applyTabRenameMapToPestanaList(
        Array.isArray(test.pestanas) ? test.pestanas : [],
        renameMap,
      ),
    };
  });
}

/**
 * Picks tab content and strips transient fields before API persist.
 * @param {Record<string, import("./temarioTree").TemarioNodo[]>} contenido
 * @param {string[]} pestanasCurso
 * @param {string[] | null | undefined} [previousTabs]
 * @returns {Record<string, import("./temarioTree").TemarioNodo[]>}
 */
export function buildContenidoPayloadForSave(contenido, pestanasCurso, previousTabs) {
  const picked = rekeyContenidoForRenamedTabs(contenido, pestanasCurso, previousTabs);
  const cloned = clonePlainObject(picked);
  stripArchivoTransientFieldsDeep(cloned);
  return cloned;
}

/**
 * Ensures every named tab has an array entry (including empty tabs).
 * @param {Record<string, import("./temarioTree").TemarioNodo[]>} contenido
 * @param {string[]} pestanasCurso
 * @returns {Record<string, import("./temarioTree").TemarioNodo[]>}
 */
export function ensureContenidoKeysForPestanas(contenido, pestanasCurso) {
  const base = contenido && typeof contenido === "object" ? { ...contenido } : {};
  for (const p of pestanasCurso) {
    const key = normalizeCourseTabName(p);
    if (!key) {
      continue;
    }
    if (!Array.isArray(base[key])) {
      base[key] = [];
    }
  }
  return base;
}

export function normalizeCourseContenido(contenidoSrc) {
  return normalizeCourseContenidoDeep(contenidoSrc);
}

function temaDataTiempoMinutos(td) {
  if (!td || Array.isArray(td)) return 0;
  const tpp = Number(td.tiempoPorPregunta);
  if (Number.isFinite(tpp)) {
    return Math.max(0, Math.round(tpp / 60));
  }
  const t = Number(td.tiempo);
  if (Number.isFinite(t)) {
    return Math.max(0, Math.round(t));
  }
  return 0;
}

function mapLikeEntries(value) {
  if (!value) {
    return [];
  }
  if (value instanceof Map) {
    return Array.from(value.entries());
  }
  if (typeof value.entries === "function" && typeof value !== "string" && !Array.isArray(value)) {
    return Array.from(value.entries());
  }
  if (typeof value === "object" && !Array.isArray(value)) {
    return Object.entries(value);
  }
  return [];
}

export function normalizeCourseTestsToArray(testsRaw) {
  if (Array.isArray(testsRaw)) {
    return testsRaw;
  }
  const testEntries = mapLikeEntries(testsRaw);
  if (testEntries.length === 0) {
    return [];
  }
  const testsArr = [];
  testEntries.forEach(([bloque, temas]) => {
    const temasEntries = mapLikeEntries(temas);
    temasEntries.forEach(([tema, td]) => {
      const preguntas = Array.isArray(td) ? td : (td?.preguntas || []);
      const tiempoMin = temaDataTiempoMinutos(td);
      testsArr.push({
        titulo: tema,
        pestanas: [bloque],
        preguntas,
        tipo: "temario",
        tiempo: tiempoMin,
        preguntasPorIntento: Array.isArray(td) ? null : (td?.preguntasPorIntento ?? null),
        notaMinima: Array.isArray(td) ? null : (td?.notaMinima ?? null),
      });
    });
  });
  return testsArr;
}
